import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MapPin, MessageCircle, Download, Calendar, Instagram, Star, Heart,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_abril-beauty-pro/artifacts/z3i5bj3p_image.png";
const BREAK_IMAGE = "https://customer-assets.emergentagent.com/job_abril-beauty-pro/artifacts/be9guu3k_image.png";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return `${DIAS[dt.getDay()]} · ${d} de ${MESES[m - 1]} · ${y}`;
  } catch {
    return iso;
  }
}

function useCountdown(iso, time) {
  const target = useMemo(() => {
    if (!iso || !time) return null;
    return new Date(`${iso}T${time}:00`).getTime();
  }, [iso, time]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  if (!target || !iso) return null;
  const diff = target - now;
  // Determine if event is "today" by comparing calendar dates
  const targetDate = new Date(iso + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);
  const isToday = targetDate.getTime() === today.getTime();
  const isPast = targetDate.getTime() < today.getTime();
  if (isPast) return { past: true };
  if (isToday || diff <= 0) return { today: true };
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  return { days, hours };
}

const SECTIONS = [
  {
    id: "cita", label: "Tu cita",
    kicker: "Los detalles",
    title: "Todo sobre tu día.",
    body: "Fecha, hora, lugar y tu QR único para llegar.",
    bg: "#F7F0E8",  // cream
    ink: "#3D2A1F",
  },
  {
    id: "antes", label: "Antes de venir",
    kicker: "Tu piel",
    title: "Cómo prepararte.",
    body: "Skin prep, descanso, hidratación — todo lo que suma antes.",
    bg: "#F7ECE7",  // blush light
    ink: "#3D2A28",
  },
  {
    id: "llegar", label: "Dónde",
    kicker: "Ubicación",
    title: "El lugar de tu cita.",
    body: "Studio, domicilio o venue — todos los detalles aquí.",
    bg: "#F1EAD8",  // beige light
    ink: "#3B341F",
  },
  {
    id: "referencia", label: "Tu look",
    kicker: "La estética",
    title: "Lo que vamos a crear.",
    body: "Las referencias visuales que acordamos juntas.",
    bg: "#E8DEC7",  // khaki light
    ink: "#2E2617",
  },
  {
    id: "pago", label: "Tu pago",
    kicker: "Tu reserva",
    title: "Estado de tu pago.",
    body: "Total, anticipo y saldo — todo transparente.",
    bg: "#F7F0E8",  // cream
    ink: "#3D2A1F",
  },
  {
    id: "politicas", label: "Políticas",
    kicker: "Sin sorpresas",
    title: "Cambios y cancelación.",
    body: "Cómo reagendar si algo se atraviesa.",
    bg: "#F7ECE7",  // blush light
    ink: "#3D2A28",
  },
  {
    id: "contacto", label: "Contacto",
    kicker: "Aquí estoy",
    title: "WhatsApp directo.",
    body: "Cualquier duda antes o después, escríbeme.",
    bg: "#F1EAD8",  // beige light
    ink: "#3B341F",
  },
  {
    id: "faq", label: "Preguntas",
    kicker: "Suele preguntarse",
    title: "Lo que otras clientas preguntan.",
    body: "Duración, pestañas, invitadas — resuelto.",
    bg: "#E8DEC7",  // khaki light
    ink: "#2E2617",
  },
  {
    id: "compartir", label: "Comparte",
    kicker: "Tu momento",
    title: "Etiquétanos.",
    body: "Instagram, TikTok — nos encanta ver tu look.",
    bg: "#F7F0E8",  // cream
    ink: "#3D2A1F",
  },
  {
    id: "reseña", label: "Reseña",
    kicker: "Después",
    title: "Tu reseña ayuda.",
    body: "Cuando ya tengas tus fotos, cuéntanos.",
    bg: "#F7ECE7",  // blush light
    ink: "#3D2A28",
  },
];

export default function StudioPass() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get(`/tickets/${token}`)
      .then(({ data }) => { if (mounted) setData(data); })
      .catch((e) => { if (mounted) setErr(formatApiError(e)); });
    return () => { mounted = false; };
  }, [token]);

  const countdown = useCountdown(data?.date, data?.start_time);

  if (err) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="overline text-[#8B6D5E] mb-4">Studio Pass · Abril Suarez</p>
          <h1 className="font-serif text-3xl mb-3">Este pase no está disponible.</h1>
          <p className="text-sm text-[#5C5C5C]">{err}. Contáctanos por WhatsApp si crees que es un error.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <p className="overline text-[#8B6D5E]">Cargando tu Studio Pass…</p>
      </div>
    );
  }

  const first = (data.client_name || "").split(" ")[0];
  const portalUrl = `${window.location.origin}/studio-pass/${token}`;
  const pdfUrl = `${BACKEND}/api/tickets/${token}/pdf`;
  const qrUrl = `${BACKEND}/api/tickets/${token}/qr.png`;
  const icsUrl = `${BACKEND}/api/tickets/${token}/calendar.ics`;
  const waHref = `https://wa.me/message/SG2ELRTG32LVF1?text=${encodeURIComponent(
    `Hola Abril! Soy ${data.client_name}, tengo una duda sobre mi cita del ${data.date}.`
  )}`;
  const mapsHref = data.location_map_url
    || (data.location_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.location_address)}` : null);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]" data-testid="studio-pass">
      <Helmet>
        <title>{`Studio Pass — ${data.client_name} · Abril Suarez`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {/* Header */}
      <div className="border-b border-[#E8E1DA] px-6 sm:px-10 py-5 flex items-center justify-between">
        <p className="overline text-[#8B6D5E]">Studio Pass · Abril Suarez</p>
        <a href={waHref} target="_blank" rel="noopener noreferrer" className="overline link-underline text-[#3C6348] hidden sm:inline">
          Escribir por WhatsApp
        </a>
      </div>

      {/* Magazine hero — big editorial image */}
      <section className="relative w-full">
        <div className="grid md:grid-cols-[45%_1fr]">
          <div className="relative h-[75vh] md:h-[85vh] overflow-hidden">
            <img
              src={HERO_IMAGE}
              alt="Abril Suarez Makeup Artist — editorial"
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute top-6 left-6 bg-[#F7F0E8]/95 backdrop-blur-sm px-4 py-2">
              <p className="overline text-[#3D2A1F]">Editorial · Abril Suarez</p>
            </div>
          </div>
          <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-16 md:py-0">
            <p className="overline text-[#8B6D5E] mb-6">Bienvenida al Studio</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              Hola, {first}.
            </h1>
            <p className="font-serif italic text-3xl sm:text-4xl lg:text-5xl text-[#8B6D5E] mt-2 mb-8">
              Estamos en la semana.
            </p>
            <div className="max-w-xl space-y-5 text-lg leading-relaxed text-[#3D3D3D]">
              <p>
                Faltan unos días para tu cita, y este pase llega antes para que
                lleguemos juntas preparadas al momento. Nada urgente — solo
                pequeños detalles que se acomodan mejor con tiempo.
              </p>
              <p className="font-serif italic text-xl sm:text-2xl text-[#1A1A1A]">
                Guárdalo, ábrelo cuando lo necesites. Aquí está todo.
              </p>
            </div>
            {countdown && countdown.days != null && (
              <p className="mt-8 overline text-[#5C5C5C]" data-testid="countdown">
                Faltan <strong className="text-[#1A1A1A]">{countdown.days} día{countdown.days === 1 ? "" : "s"}</strong>
                {countdown.hours > 0 && <> y <strong className="text-[#1A1A1A]">{countdown.hours} h</strong></>}
                {" "}para tu cita
              </p>
            )}
            {countdown?.today && (
              <p className="mt-8 overline text-[#3C6348]" data-testid="countdown-today">
                — Hoy es el día · Te esperamos —
              </p>
            )}
            {countdown?.past && (
              <p className="mt-8 overline text-[#8B6D5E]" data-testid="countdown-past">
                — Gracias por dejarnos ser parte de tu momento —
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ─── TICKET BLOCK ─── */}
      <section id="cita" className="px-6 sm:px-10 pb-16 max-w-5xl mx-auto scroll-mt-24">
        <div className="bg-[#F4F0EC] p-6 sm:p-10 grid md:grid-cols-[1fr_auto] gap-8 md:gap-10 items-center">
          <div className="space-y-5">
            <div>
              <p className="overline text-[#8B6D5E] mb-1">Servicio</p>
              <p className="font-serif text-2xl" data-testid="pass-service">{data.service_title}</p>
            </div>
            <div>
              <p className="overline text-[#8B6D5E] mb-1">Fecha</p>
              <p className="font-serif text-2xl">{fmtDate(data.date)}</p>
            </div>
            <div>
              <p className="overline text-[#8B6D5E] mb-1">Hora</p>
              <p className="font-serif text-2xl">{data.start_time} — {data.end_time}</p>
              <p className="text-sm text-[#5C5C5C]">Duración estimada: {data.duration_minutes} minutos</p>
            </div>
            {data.location_address && (
              <div>
                <p className="overline text-[#8B6D5E] mb-1">Ubicación</p>
                <p className="text-base">{data.location_address}</p>
                {mapsHref && (
                  <a href={mapsHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 overline link-underline text-[#3C6348] mt-2">
                    <MapPin size={14} /> Abrir en Google Maps
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="bg-[#FAFAFA] p-5 flex flex-col items-center gap-3 md:min-w-[210px]">
            <img src={qrUrl} alt="QR Studio Pass" className="w-[180px] h-[180px]" data-testid="pass-qr" />
            <p className="overline text-[#8B6D5E]">Muestra este QR al llegar</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 grid sm:grid-cols-3 gap-2">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between border border-[#1A1A1A]/15 hover:border-[#1A1A1A] px-5 py-4" data-testid="pass-download">
            <span className="overline">Descargar en PDF</span><Download size={16} />
          </a>
          <a href={icsUrl} className="flex items-center justify-between border border-[#1A1A1A]/15 hover:border-[#1A1A1A] px-5 py-4" data-testid="pass-calendar">
            <span className="overline">Agregar a calendario</span><Calendar size={16} />
          </a>
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between border border-[#1A1A1A]/15 hover:border-[#1A1A1A] px-5 py-4" data-testid="pass-whatsapp">
            <span className="overline">Escribir por WhatsApp</span><MessageCircle size={16} />
          </a>
        </div>

        {data.client_notes && (
          <div className="mt-10 border-l-2 border-[#8B6D5E] pl-6 py-2">
            <p className="overline text-[#8B6D5E] mb-2">Un mensaje de Abril</p>
            <p className="font-serif italic text-xl leading-relaxed text-[#1A1A1A]">&ldquo;{data.client_notes}&rdquo;</p>
          </div>
        )}
      </section>

      {/* ─── NAV GRID — colored boxes ─── */}
      <section className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-6xl mx-auto">
        <p className="overline text-[#8B6D5E] mb-3">Tu experiencia completa</p>
        <h2 className="font-serif text-4xl sm:text-5xl mb-3 leading-tight">
          Todo lo que necesitas
        </h2>
        <p className="font-serif italic text-3xl sm:text-4xl text-[#8B6D5E] mb-12 leading-tight">
          en un solo lugar.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SECTIONS.map((sec) => (
            <a
              key={sec.id}
              href={`#${sec.id}`}
              className="group relative block p-7 sm:p-8 transition-transform duration-300 hover:-translate-y-1"
              style={{ backgroundColor: sec.bg, color: sec.ink }}
              data-testid={`section-card-${sec.id}`}
            >
              <p className="overline mb-8 opacity-70">— {sec.label}</p>
              <p className="text-[10px] tracking-[0.22em] uppercase opacity-60 mb-2">{sec.kicker}</p>
              <p className="font-serif text-2xl leading-tight mb-3">{sec.title}</p>
              <p className="text-sm leading-relaxed opacity-80 mb-6">{sec.body}</p>
              <p className="overline flex items-center gap-2 opacity-80 group-hover:opacity-100">
                Ver
                <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
              </p>
            </a>
          ))}
        </div>
      </section>

      {/* ─── EDITORIAL BREAK ─── */}
      <section className="w-full">
        <div className="relative h-[55vh] md:h-[70vh] overflow-hidden">
          <img
            src={BREAK_IMAGE}
            alt="Editorial — Abril Suarez"
            className="w-full h-full object-cover"
            data-testid="break-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-6 sm:left-10 max-w-md">
            <p className="overline text-[#F7F0E8] mb-3">Detrás del pincel</p>
            <p className="font-serif italic text-3xl sm:text-4xl text-[#FAFAFA] leading-tight">
              Cada mirada, un detalle pensado.
            </p>
          </div>
        </div>
      </section>

      {/* ─── EMOTIONAL SECTION ─── */}
      <section className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-4xl mx-auto text-center">
        <p className="overline text-[#8B6D5E] mb-6">Entre nosotras</p>
        <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-8">
          Los mejores maquillajes <em className="text-[#8B6D5E]">empiezan antes.</em>
        </h2>
        <p className="text-lg leading-relaxed text-[#3D3D3D] max-w-2xl mx-auto">
          No en la silla del maquillaje — en los días previos. En cómo duermes,
          cómo te hidratas, cómo llegas al día de tu cita. Por eso te comparto este pase con anticipación:
          para que llegues sin prisas, sin dudas, sin nada pendiente.
        </p>
        <p className="mt-8 font-serif italic text-2xl text-[#8B6D5E]">
          El resto — el maquillaje — es lo mío.
        </p>
      </section>

      {/* ─── ANTES DE VENIR ─── */}
      {data.checklist?.length > 0 && (
        <section id="antes" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
          <p className="overline text-[#8B6D5E] mb-3">Antes de venir</p>
          <h2 className="font-serif text-4xl sm:text-5xl mb-3">Empecemos esta semana.</h2>
          <p className="font-serif italic text-2xl sm:text-3xl text-[#8B6D5E] mb-6">Tu piel se prepara desde hoy.</p>
          <p className="text-lg leading-relaxed text-[#3D3D3D] max-w-2xl mb-12">
            No hace falta gastar en productos nuevos — solo seguir estos pequeños detalles que suman.
            Cada punto está pensado para que el día del evento tu piel se vea real, luminosa,
            y el maquillaje aguante hasta que apagues la última luz.
          </p>
          <div className="grid md:grid-cols-2 gap-x-12">
            {data.checklist.map((c, i) => (
              <div key={i} className="border-t border-[#E8E1DA] py-6">
                <p className="overline text-[#8B6D5E] mb-2">
                  {c.days_before === 0 ? "Día de tu cita" : c.days_before === 1 ? "1 día antes" : c.days_before ? `${c.days_before} días antes` : "Cuando puedas"}
                </p>
                <p className="font-serif text-xl mb-1">{c.title}</p>
                {c.detail && <p className="text-sm text-[#5C5C5C] leading-relaxed">{c.detail}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── CÓMO LLEGAR ─── */}
      <section id="llegar" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
        <p className="overline text-[#8B6D5E] mb-3">
          {data.location_type === "domicilio" ? "Servicio a domicilio"
            : data.location_type === "venue" ? "El día del evento"
            : "Cómo llegar"}
        </p>
        <h2 className="font-serif text-4xl sm:text-5xl mb-3">
          {data.location_type === "domicilio" ? "Yo voy a ti."
            : data.location_type === "venue" ? "Nos vemos en el venue."
            : "Nos vemos aquí."}
        </h2>
        <p className="font-serif italic text-2xl sm:text-3xl text-[#8B6D5E] mb-6">
          {data.location_type === "domicilio" ? "Sin que tengas que salir."
            : data.location_type === "venue" ? "Directo al lugar de tu evento."
            : "Todo listo para recibirte."}
        </p>
        {data.location_address ? (
          <div className="mt-6 space-y-6">
            <div>
              <p className="overline text-[#8B6D5E] mb-2">
                {data.location_type === "domicilio" ? "Tu dirección" : "Dirección"}
              </p>
              <p className="font-serif text-2xl leading-tight">{data.location_address}</p>
            </div>
            {data.location_notes && (
              <div>
                <p className="overline text-[#8B6D5E] mb-2">Detalles</p>
                <p className="text-base text-[#3D3D3D] leading-relaxed">{data.location_notes}</p>
              </div>
            )}
            {mapsHref && data.location_type !== "domicilio" && (
              <a href={mapsHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAFAFA] px-6 py-3 overline">
                <MapPin size={14} /> Abrir en Google Maps
              </a>
            )}
            {data.location_type === "domicilio" && (
              <p className="text-sm text-[#5C5C5C] italic">
                Llegaré con todo mi kit. Solo necesito una silla con buena luz natural y un espacio limpio cerca de un enchufe.
              </p>
            )}
          </div>
        ) : (
          <p className="text-[#5C5C5C]">La ubicación se compartirá por WhatsApp antes de tu cita.</p>
        )}
      </section>

      {/* ─── LOOK DE REFERENCIA ─── */}
      {data.reference_images?.length > 0 && (
        <section id="referencia" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
          <p className="overline text-[#8B6D5E] mb-3">Tu look de referencia</p>
          <h2 className="font-serif text-4xl mb-3">Así vamos a construir tu maquillaje.</h2>
          <p className="text-[#5C5C5C] max-w-2xl mb-10">
            Estas son las referencias que acordamos juntas. Son la base creativa — el día del evento las adaptamos a tu piel, luz y ocasión.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.reference_images.map((img, i) => (
              <figure key={i}>
                <img src={img.url} alt={img.caption || `Referencia ${i + 1}`} className="w-full aspect-[3/4] object-cover" />
                {img.caption && <figcaption className="text-xs text-[#5C5C5C] mt-2">{img.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* ─── PAGO ─── */}
      <section id="pago" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
        <p className="overline text-[#8B6D5E] mb-3">Tu pago</p>
        <h2 className="font-serif text-4xl mb-8">Estado de tu reserva.</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          <div>
            <p className="overline text-[#8B6D5E] mb-2">Estado</p>
            <p className="font-serif text-2xl capitalize">
              {data.payment_status === "paid" ? "Pagado ✓" : data.payment_status === "deposit" ? "Anticipo recibido" : "Pendiente"}
            </p>
          </div>
          {data.amount_total != null && (
            <div>
              <p className="overline text-[#8B6D5E] mb-2">Total</p>
              <p className="font-serif text-2xl">${data.amount_total.toLocaleString("es-MX")} MXN</p>
            </div>
          )}
          {data.amount_paid != null && (
            <div>
              <p className="overline text-[#8B6D5E] mb-2">Pagado</p>
              <p className="font-serif text-2xl">${data.amount_paid.toLocaleString("es-MX")} MXN</p>
            </div>
          )}
        </div>
      </section>

      {/* ─── POLÍTICAS ─── */}
      <section id="politicas" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
        <p className="overline text-[#8B6D5E] mb-3">Políticas</p>
        <h2 className="font-serif text-4xl mb-8">Cambios y cancelación.</h2>
        <ul className="space-y-3 text-base leading-relaxed">
          <li className="border-b border-[#E8E1DA] pb-3">• Cambios de fecha se aceptan con mínimo <strong>72 horas de anticipación</strong>, sujetos a disponibilidad.</li>
          <li className="border-b border-[#E8E1DA] pb-3">• Cancelaciones con más de 15 días de anticipación: reembolso del 50% del anticipo.</li>
          <li className="border-b border-[#E8E1DA] pb-3">• Cancelaciones con menos de 15 días: el anticipo no es reembolsable.</li>
          <li className="border-b border-[#E8E1DA] pb-3">• Si Abril debe cancelar por fuerza mayor: reembolso total o reprogramación sin costo.</li>
          <li className="border-b border-[#E8E1DA] pb-3">• Puntualidad: llegar 10 minutos antes. Tolerancia máxima 20 minutos — después se ajusta el servicio al tiempo restante.</li>
        </ul>
      </section>

      {/* ─── CONTACTO ─── */}
      <section id="contacto" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
        <p className="overline text-[#8B6D5E] mb-3">Contacto directo</p>
        <h2 className="font-serif text-4xl sm:text-5xl mb-3">Estoy aquí.</h2>
        <p className="font-serif italic text-2xl sm:text-3xl text-[#8B6D5E] mb-6">Sin filtros, sin agencias.</p>
        <p className="text-lg leading-relaxed text-[#3D3D3D] max-w-2xl mb-8">
          Cualquier duda antes, durante o después de tu cita — escríbeme directo por WhatsApp.
          Respondo personalmente de lunes a sábado, 9am–7pm. Si escribes fuera de ese horario,
          respondo lo más pronto al día siguiente.
        </p>
        <a href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAFAFA] px-6 py-3 overline">
          <MessageCircle size={14} /> Abrir WhatsApp
        </a>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
        <p className="overline text-[#8B6D5E] mb-3">Preguntas frecuentes</p>
        <h2 className="font-serif text-4xl sm:text-5xl mb-10">Lo que suele preguntarse.</h2>
        <div className="space-y-6">
          {[
            ["¿Cuánto dura el servicio?", `Aproximadamente ${data.duration_minutes} minutos. Considera 15 min extra para últimos detalles antes de salir.`],
            ["¿Puedo llegar con base o skincare puesto?", "Piel limpia y humectada con crema muy ligera. Sin base, sin SPF pesado — puede interferir con el maquillaje."],
            ["¿Incluye pestañas postizas?", "Sí, pestañas de calidad incluidas. Si prefieres no usarlas o traes tus propias extensiones, coordinamos."],
            ["¿Y si el día del evento estoy nerviosa?", "Es lo más normal del mundo. Vas a llegar, respirar, escuchar buena música, y salir con la piel más luminosa que has tenido."],
            ["¿Puedo llevar a alguien?", "Sí, una acompañante. Si vienen invitadas para maquillarse también, tienen 10% con tu código de referida — pregúntame."],
          ].map(([q, a]) => (
            <details key={q} className="border-b border-[#E8E1DA] pb-4">
              <summary className="font-serif text-xl cursor-pointer">{q}</summary>
              <p className="mt-3 text-base text-[#3D3D3D] leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ─── COMPARTE ─── */}
      <section id="compartir" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
        <p className="overline text-[#8B6D5E] mb-3">Comparte tu momento</p>
        <h2 className="font-serif text-4xl sm:text-5xl mb-3">Etiquétanos.</h2>
        <p className="font-serif italic text-2xl sm:text-3xl text-[#8B6D5E] mb-6">Tu look también inspira a otras.</p>
        <p className="text-[#5C5C5C] max-w-2xl mb-6 leading-relaxed">
          Si compartes fotos ese día en Instagram o TikTok, etiqueta a Abril — nos encanta ver el resultado y compartir tu look con la comunidad.
        </p>
        <div className="space-y-2">
          <a href="https://instagram.com/abrilsuamkp" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 overline link-underline">
            <Instagram size={14} /> @abrilsuamkp
          </a>
          <p className="text-sm text-[#5C5C5C]">Hashtag oficial: <strong className="text-[#1A1A1A]">#AbrilMakeup</strong></p>
        </div>
      </section>

      {/* ─── RESEÑA ─── */}
      <section id="reseña" className="px-6 sm:px-10 py-20 border-t border-[#E8E1DA] max-w-5xl mx-auto scroll-mt-24">
        <p className="overline text-[#8B6D5E] mb-3">Después de tu cita</p>
        <h2 className="font-serif text-4xl sm:text-5xl mb-3">Cuéntame cómo te sentiste.</h2>
        <p className="font-serif italic text-2xl sm:text-3xl text-[#8B6D5E] mb-6">Tu voz vale más que cualquier campaña.</p>
        <p className="text-lg leading-relaxed text-[#3D3D3D] max-w-2xl mb-8">
          Cuando termines tu evento y guardes las fotos que te gusten,
          si disfrutaste el servicio, deja una reseña en Google.
          Es lo que le da confianza a las siguientes novias, XVs e invitadas
          que están buscando makeup artist — y para mí, es el mayor regalo.
        </p>
        <a
          href="https://g.page/r/abrilsuarezmakeup/review"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-[#1A1A1A] px-6 py-3 overline hover:bg-[#1A1A1A] hover:text-[#FAFAFA]"
        >
          <Star size={14} /> Escribir reseña en Google
        </a>
      </section>

      {/* ─── EMOTIONAL CLOSING ─── */}
      <section className="px-6 sm:px-10 py-24 bg-[#F7F0E8] mt-16">
        <div className="max-w-3xl mx-auto text-center">
          <p className="overline text-[#8B6D5E] mb-6">Una última cosa</p>
          <p className="font-serif text-3xl sm:text-4xl leading-tight text-[#2E2617] mb-8">
            Gracias por elegirme, {first}.
          </p>
          <p className="text-lg leading-relaxed text-[#3D2A1F] max-w-2xl mx-auto">
            Sé que hay muchas opciones ahí afuera. Que confíes tu cara — literalmente —
            a mis manos es algo que nunca doy por sentado.
            Prometo cuidarte con la atención que este momento merece.
          </p>
          <p className="mt-8 font-serif italic text-2xl text-[#8B6D5E]">Con cariño, Abril.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 sm:px-10 py-10 border-t border-[#E8E1DA]">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="overline text-[#8B6D5E]">Abril Suarez · Makeup Artist · Aguascalientes</p>
          <p className="overline text-[#5C5C5C] flex items-center gap-2">
            <Heart size={12} className="text-[#8B6D5E]" /> Hecho con cariño para ti, {first}.
          </p>
        </div>
      </footer>
    </div>
  );
}
